import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ProjectTab {
  /** Stable key used for the active comparison and passed back to onSelect. */
  key: string;
  /** Already-translated label (shown next to the icon, and under it on mobile). */
  label: string;
  /** Feather icon name without the `feather-` prefix. */
  icon: string;
}

export interface ProjectTabsBarProps {
  tabs: ProjectTab[];
  activeKey: string;
  onSelect: (key: string) => void;
}

/**
 * Project-view tab bar: underlined tabs (`project-tabs`) with icon + label.
 * On mobile the label sits under the icon and the row is swipeable; a fade and
 * chevron on each edge show when there are more tabs to scroll to. Keyboard:
 * Left/Right/Home/End move focus and select (automatic activation).
 *
 * The caller supplies the tab list (already translated, already filtered) —
 * this component decides nothing about visibility or permissions.
 */
export function ProjectTabsBar({ tabs, activeKey, onSelect }: ProjectTabsBarProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollPrev(el.scrollLeft > 1);
    setCanScrollNext(el.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    updateOverflow();
    el.addEventListener('scroll', updateOverflow, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateOverflow);
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', updateOverflow);
      observer?.disconnect();
    };
  }, [updateOverflow, tabs.length]);

  // Keep the active tab visible in the swipeable strip (on mount and on change).
  useEffect(() => {
    const index = tabs.findIndex(tab => tab.key === activeKey);
    const button = index >= 0 ? tabRefs.current[index] : null;
    if (button && typeof button.scrollIntoView === 'function') {
      button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    updateOverflow();
  }, [activeKey, tabs, updateOverflow]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = Math.max(el.clientWidth * 0.6, 80) * direction;
    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ left: distance, behavior: 'smooth' });
    } else {
      el.scrollLeft += distance;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        next = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    tabRefs.current[next]?.focus();
    onSelect(tabs[next].key);
  };

  const wrapperClass = [
    'project-tabs',
    canScrollPrev ? 'has-prev' : '',
    canScrollNext ? 'has-next' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClass}>
      <div className="project-tabs-scroll" ref={scrollRef}>
        <ul className="project-tabs-list" role="tablist" aria-label={t('projectTabs.label', 'Projektets flikar')}>
          {tabs.map((tab, index) => {
            const isActive = activeKey === tab.key;
            return (
              <li key={tab.key} className="project-tabs-item" role="presentation">
                <button
                  type="button"
                  ref={node => { tabRefs.current[index] = node; }}
                  role="tab"
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  className={`nav-link project-tabs-link ${isActive ? 'active' : ''}`}
                  onClick={() => onSelect(tab.key)}
                  onKeyDown={event => handleKeyDown(event, index)}
                >
                  <i className={`feather-${tab.icon} project-tabs-icon`} aria-hidden="true"></i>
                  <span className="project-tabs-label">{tab.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      {canScrollPrev && (
        <button
          type="button"
          className="project-tabs-chevron project-tabs-chevron-prev"
          tabIndex={-1}
          aria-label={t('projectTabs.scrollPrev', 'Visa tidigare flikar')}
          onClick={() => scrollByPage(-1)}
        >
          <i className="feather-chevron-left" aria-hidden="true"></i>
        </button>
      )}
      {canScrollNext && (
        <button
          type="button"
          className="project-tabs-chevron project-tabs-chevron-next"
          tabIndex={-1}
          aria-label={t('projectTabs.scrollNext', 'Visa fler flikar')}
          onClick={() => scrollByPage(1)}
        >
          <i className="feather-chevron-right" aria-hidden="true"></i>
        </button>
      )}
    </div>
  );
}
